'use dom';
import { useEffect, useRef, forwardRef, useImperativeHandle, useState } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css'; // Import Quill's snow theme styles
import { SHOW_HELP_MODAL } from '@reduxLocal/actions/types';
import { useDispatch, useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';


// Define the methods you want to expose to the parent component
export type DomEditorHandle = {
  getContent: () => string | null;
  setContent: (html: string) => void;
};

type EditorColors = {
  background: string;
  surface: string;
  border: string;
  text: string;
  accent: string;
  toolbar: string;
  toolbarBorder: string;
  shadow: string;
};

type DomEditorProps = {
  saveFn: (content: string) => Promise<void>;
  backFn?: () => void;
  hideBack?: boolean;
  initialContent?: string; // Added prop for initial content
  editorColors?: EditorColors; // Theme colors for styling
  removeButtons?: boolean;
};

const DomEditor = forwardRef<DomEditorHandle, DomEditorProps>(function DomEditor(
  { hideBack, saveFn, backFn, initialContent = '', editorColors, removeButtons = false },
  ref
) {
  // Use local refs for DOM elements to avoid relying on IDs
  const editorRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  // Use a ref to hold the Quill instance
  const dispatch = useDispatch();
  const quillRef = useRef<Quill | null>(null);
  // Static glow effect - no continuous animation for better performance
  const [isEditorActive, setIsEditorActive] = useState(false);
  
  // Get theme state from Redux
  const theme = themeSelector();
  
  // Default colors if not provided via props
  const colors = editorColors || {
    background: theme ? '#ffffff' : '#1a1a1a',
    surface: theme ? '#f8f9fa' : '#2d2d2d',
    border: theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
    text: theme ? '#333333' : '#e0e0e0',
    accent: theme ? '#7c3aed' : '#a78bfa',
    toolbar: theme ? '#ffffff' : '#252525',
    toolbarBorder: theme ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)',
    shadow: theme 
      ? '0 2px 8px rgba(0, 0, 0, 0.1)' 
      : '0 2px 8px rgba(0, 0, 0, 0.3)',
  };

  // Theme-aware glow styles
  const getGlowStyles = () => {
    return {
      boxShadow: isEditorActive 
        ? `0 0 10px ${colors.accent}40, 0 0 20px ${colors.accent}30, inset 0 0 5px ${colors.accent}20`
        : colors.shadow,
      border: `2px solid ${colors.accent}${isEditorActive ? 'B3' : '80'}`,
      borderRadius: '12px',
      transition: 'all 0.3s ease-in-out',
      backgroundColor: colors.background,
    };
  };

  // Add theme-aware CSS for animations and Quill styling
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'dom-editor-theme-styles';
    style.textContent = `
      @keyframes domEditorPulse {
        0%, 100% { 
          transform: scale(1); 
          opacity: 0.9; 
        }
        50% { 
          transform: scale(1.02); 
          opacity: 1; 
        }
      }
      
      .dom-editor-ai-badge {
        animation: domEditorPulse 3s ease-in-out infinite;
        background: ${colors.accent}E6 !important;
        box-shadow: 0 0 8px ${colors.accent}66 !important;
      }
      
      /* Toolbar Styling */
      .dom-editor-container .ql-toolbar {
        border-top: none !important;
        border-left: none !important;
        border-right: none !important;
        border-bottom: 1px solid ${colors.toolbarBorder} !important;
        background: ${colors.toolbar} !important;
        border-radius: 12px 12px 0 0 !important;
      }
      
      /* Container Styling */
      .dom-editor-container .ql-container {
        border: none !important;
        background: ${colors.background} !important;
        color: ${colors.text} !important;
        border-radius: 0 0 12px 12px !important;
      }
      
      /* Editor Content Area */
      .dom-editor-container .ql-editor {
        background: ${colors.background} !important;
        color: ${colors.text} !important;
        min-height: 200px !important;
        padding: 20px !important;
        font-size: 16px !important;
        line-height: 1.6 !important;
      }
      
      .dom-editor-container .ql-editor:focus {
        outline: none !important;
        box-shadow: inset 0 0 10px ${colors.accent}20 !important;
      }
      
      /* Toolbar Button Styling */
      .dom-editor-container .ql-toolbar .ql-formats .ql-picker-label,
      .dom-editor-container .ql-toolbar button {
        color: ${colors.text} !important;
        transition: all 0.2s ease !important;
      }
      
      .dom-editor-container .ql-toolbar .ql-formats .ql-picker-label:hover,
      .dom-editor-container .ql-toolbar button:hover {
        color: ${colors.accent} !important;
        background: ${colors.accent}15 !important;
        border-radius: 4px !important;
      }
      
      .dom-editor-container .ql-toolbar button.ql-active {
        color: ${colors.accent} !important;
        background: ${colors.accent}20 !important;
        border-radius: 4px !important;
      }
      
      /* Dropdown and Picker Styling */
      .dom-editor-container .ql-picker-options {
        background: ${colors.surface} !important;
        border: 1px solid ${colors.border} !important;
        border-radius: 8px !important;
        box-shadow: ${colors.shadow} !important;
      }
      
      .dom-editor-container .ql-picker-item {
        color: ${colors.text} !important;
        padding: 8px 12px !important;
      }
      
      .dom-editor-container .ql-picker-item:hover {
        background: ${colors.accent}15 !important;
        color: ${colors.accent} !important;
      }
      
      /* Selection and Highlight Colors */
      .dom-editor-container .ql-editor ::selection {
        background: ${colors.accent}30 !important;
      }
      
      /* Placeholder Text */
      .dom-editor-container .ql-editor.ql-blank::before {
        color: ${theme ? '#999999' : '#666666'} !important;
        font-style: italic !important;
      }
      
      /* Custom scrollbar for dark theme */
      .dom-editor-container .ql-editor::-webkit-scrollbar {
        width: 8px;
      }
      
      .dom-editor-container .ql-editor::-webkit-scrollbar-track {
        background: ${colors.surface};
        border-radius: 4px;
      }
      
      .dom-editor-container .ql-editor::-webkit-scrollbar-thumb {
        background: ${colors.border};
        border-radius: 4px;
        transition: background 0.2s ease;
      }
      
      .dom-editor-container .ql-editor::-webkit-scrollbar-thumb:hover {
        background: ${colors.accent}80;
      }
    `;
    
    // Remove existing style if it exists
    const existingStyle = document.getElementById('dom-editor-theme-styles');
    if (existingStyle) {
      document.head.removeChild(existingStyle);
    }
    
    document.head.appendChild(style);
    
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, [colors, theme]); // Re-run when colors or theme change

  // Initialize and clean up Quill - optimized for performance
  useEffect(() => {
    // Ensure refs are connected and Quill isn't already initialized
    if (editorRef.current && toolbarRef.current && !quillRef.current) {
      const quill = new Quill(editorRef.current, {
        theme: 'snow',
        modules: {
          toolbar: toolbarRef.current, // Link the toolbar
        },
        placeholder: 'Start writing...',
        // Performance optimizations
        formats: [
          'bold', 'italic', 'underline', 'strike', 'color', 'background',
          'header', 'blockquote', 'code-block', 'list', 'bullet', 'indent',
          'link', 'image', 'video', 'align', 'direction', 'font', 'size'
        ],
      });
      
      quillRef.current = quill; // Store the instance

      // Set initial content if provided
      if (initialContent) {
        quill.clipboard.dangerouslyPasteHTML(initialContent);
      }

      // Optimize editor container styling - theme aware
      const editorContainer = editorRef.current.querySelector('.ql-editor');
      if (editorContainer) {
        const editorElement = editorContainer as HTMLElement;
        editorElement.style.cssText = `
          height: 100%;
          min-height: 200px;
          overflow: auto;
          background: ${colors.background};
          color: ${colors.text};
          transition: all 0.2s ease;
          padding: 20px;
          font-size: 16px;
          line-height: 1.6;
        `;
      }

      // Optimize Quill container styling - theme aware
      const quillContainer = editorRef.current.querySelector('.ql-container');
      if (quillContainer) {
        const containerElement = quillContainer as HTMLElement;
        containerElement.style.cssText = `
          height: 100%;
          display: flex;
          flex-direction: column;
          border-radius: 0 0 12px 12px;
          overflow: hidden;
          border: none !important;
          background: ${colors.background};
        `;
      }

      // Theme-aware focus/blur handlers with performance optimization
      const handleFocus = () => {
        setIsEditorActive(true);
        if (editorContainer) {
          (editorContainer as HTMLElement).style.background = `${colors.background}`;
          (editorContainer as HTMLElement).style.boxShadow = `inset 0 0 10px ${colors.accent}20`;
        }
      };

      const handleBlur = () => {
        setIsEditorActive(false);
        if (editorContainer) {
          (editorContainer as HTMLElement).style.background = colors.background;
          (editorContainer as HTMLElement).style.boxShadow = 'none';
        }
      };

      // Debounced content change handler for performance
      let contentChangeTimeout: NodeJS.Timeout;
      const handleContentChange = () => {
        clearTimeout(contentChangeTimeout);
        contentChangeTimeout = setTimeout(() => {
          // Only trigger if content actually changed
          const currentContent = quill.root.innerHTML;
          if (currentContent !== (quill as any).lastContent) {
            (quill as any).lastContent = currentContent;
            // Trigger any necessary updates here
          }
        }, 100); // Debounce content changes by 100ms
      };

      quill.root.addEventListener('focus', handleFocus);
      quill.root.addEventListener('blur', handleBlur);
      quill.on('text-change', handleContentChange);

      // Store event listeners for cleanup
      (quill as any).customEventListeners = {
        handleFocus,
        handleBlur,
        handleContentChange,
        contentChangeTimeout
      };
    }

    // Cleanup on unmount
    return () => {
      // Clean up custom event listeners
      if (quillRef.current && (quillRef.current as any).customEventListeners) {
        const { handleFocus, handleBlur, handleContentChange, contentChangeTimeout } = (quillRef.current as any).customEventListeners;
        quillRef.current.root.removeEventListener('focus', handleFocus);
        quillRef.current.root.removeEventListener('blur', handleBlur);
        quillRef.current.off('text-change', handleContentChange);
        if (contentChangeTimeout) {
          clearTimeout(contentChangeTimeout);
        }
      }
      
      // No official destroy method, so we nullify the ref and clear the editor
      quillRef.current = null;
      if (editorRef.current) {
        editorRef.current.innerHTML = "";
      }
    };
  }, [colors]); // Only re-run when colors change, not initialContent

  // Handle content updates when initialContent changes
  useEffect(() => {
    if (quillRef.current && initialContent) {
      quillRef.current.clipboard.dangerouslyPasteHTML(initialContent);
    }
  }, [initialContent]);

  // Expose specific functions to the parent component via the forwarded ref
  useImperativeHandle(ref, () => ({
    getContent: () => {
      return quillRef.current ? quillRef.current.root.innerHTML : null;
    },
    setContent: (html: string) => {
      if (quillRef.current) {
        quillRef.current.root.innerHTML = html;
      }
    },
  }));

  const handleSave = async () => {
    const content = quillRef.current?.root.innerHTML;
    if (content !== null && content !== undefined) {
      console.log("Content to save:", content);
      await saveFn(content);
    } else {
      console.error("Editor is not initialized or is empty.");
    }
  };

  return (
    <div className="dashboard-main-body" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div 
        className="card basic-data-table radius-12 overflow-hidden dom-editor-container" 
        style={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          position: 'relative',
          ...getGlowStyles()
        }}
      >
        {/* AI Indicator Badge - CSS animation only */}
        <div
          onClick={() => {
            dispatch({ type: SHOW_HELP_MODAL })
          }}
          className="dom-editor-ai-badge"
          style={{
            position: 'absolute',
            top: '10px',
            right: '15px',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: '600',
            zIndex: 10,
            cursor: 'pointer'
          }}
        >
          ✨ AI Ready
        </div>

        <div className="card-body p-0" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Toolbar: Attach a ref instead of using an ID */}
          <div ref={toolbarRef} style={{ flexShrink: 0 }}>
            <span className="ql-formats">
              <select className="ql-font"></select>
              <select className="ql-size"></select>
            </span>
            <span className="ql-formats">
              <button className="ql-bold"></button>
              <button className="ql-italic"></button>
              <button className="ql-underline"></button>
              <button className="ql-strike"></button>
            </span>
            <span className="ql-formats">
              <select className="ql-color"></select>
              <select className="ql-background"></select>
            </span>
            <span className="ql-formats">
              <button className="ql-script" value="sub"></button>
              <button className="ql-script" value="super"></button>
            </span>
            <span className="ql-formats">
              <button className="ql-header" value="1"></button>
              <button className="ql-header" value="2"></button>
              <button className="ql-blockquote"></button>
              <button className="ql-code-block"></button>
            </span>
            <span className="ql-formats">
              <button className="ql-list" value="ordered"></button>
              <button className="ql-list" value="bullet"></button>
              <button className="ql-indent" value="-1"></button>
              <button className="ql-indent" value="+1"></button>
            </span>
            <span className="ql-formats">
              <button className="ql-direction" value="rtl"></button>
              <select className="ql-align"></select>
            </span>
            <span className="ql-formats">
              <button className="ql-link"></button>
              <button className="ql-image"></button>
              <button className="ql-video"></button>
              <button className="ql-formula"></button>
            </span>
            <span className="ql-formats">
              <button className="ql-clean"></button>
            </span>
          </div>
          {/* Editor Container: Attach a ref instead of using an ID */}
          <div 
            ref={editorRef} 
            style={{ 
              flex: 1, 
              minHeight: 0, 
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column'
            }}
          ></div>
        </div>
        {!removeButtons && (
        <div className="card-footer p-24 bg-base border border-bottom-0 border-end-0 border-start-0" style={{ flexShrink: 0 }}>
          <div className="d-flex align-items-center justify-content-center gap-3">
            {!hideBack && (
              <button
                type="button"
                onClick={backFn}
                className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-50 py-11 radius-8"
                style={{
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 0 8px rgba(220, 38, 38, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              onClick={handleSave}
              className="btn btn-primary border border-primary-600 text-md px-28 py-12 radius-8"
              style={{
                background: `linear-gradient(135deg, rgb(147, 51, 234), rgb(126, 34, 206))`,
                borderColor: 'rgba(147, 51, 234, 0.9)',
                boxShadow: '0 0 10px rgba(147, 51, 234, 0.3)',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 0 20px rgba(147, 51, 234, 0.5)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 0 10px rgba(147, 51, 234, 0.3)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Save Changes
            </button>
          </div>
        </div>
        )}
      </div>
    </div>
  );
});

export default DomEditor;